/**
 * Admin - Invite User Page
 *
 * Send an invitation to a new user. The user signs up on their own
 * (Google or email) and is automatically matched to the invitation
 * on first login. Attribution is tracked for referrals/discounts.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../platform/api';
import {
  ArrowLeft,
  Check,
  User,
  Shield,
  Mail,
  MailX,
  UserPlus,
  Copy,
  CreditCard,
} from 'lucide-react';

interface BillingPlan {
  id: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  billing_interval: string;
}

interface InvitationResponse {
  data: {
    id: string;
    email: string;
    name: string;
    role: string;
    status: string;
    expires_at: string;
    email_sent?: boolean;
    email_error?: string;
    metadata?: { plan_id?: string; plan_name?: string; trial_days?: number };
  };
  invitation_link: string;
}

export function AdminUserCreatePage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('free');
  const [trialDays, setTrialDays] = useState<number>(14);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;
  const isPaidPlan = selectedPlan ? selectedPlan.price_cents > 0 : false;

  // Load available plans
  useEffect(() => {
    apiRequest<{ data: BillingPlan[] }>('/api/billing/plans')
      .then((res) => {
        const fetched = res.data || [];
        setPlans(fetched);
      })
      .catch(() => {
        // Fallback — at minimum show free
        setPlans([{ id: 'free', name: 'Free', description: '', price_cents: 0, currency: 'eur', billing_interval: 'month' }]);
      });
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Persist invitation result in sessionStorage to survive auth remounts.
  // The auth token refresh causes a remount BEFORE the API response arrives,
  // so the old closure writes to sessionStorage and dispatches a custom event
  // that the new (remounted) component picks up via useEffect.
  const SESSION_KEY = 'admin_invitation_result';
  const INVITATION_EVENT = 'admin-invitation-created';
  const [invitation, setInvitationState] = useState<InvitationResponse | null>(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      try { return JSON.parse(stored); } catch { return null; }
    }
    return null;
  });

  useEffect(() => {
    const handler = () => {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored) {
        try { setInvitationState(JSON.parse(stored)); } catch { /* ignore */ }
      }
    };
    window.addEventListener(INVITATION_EVENT, handler);
    return () => window.removeEventListener(INVITATION_EVENT, handler);
  }, []);

  const setInvitation = (inv: InvitationResponse | null) => {
    if (inv) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(inv));
      window.dispatchEvent(new Event(INVITATION_EVENT));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
    setInvitationState(inv);
  };

  const roles = [
    {
      value: 'user' as const,
      label: 'User',
      description: 'Can create content and manage their own data',
      icon: User,
    },
    {
      value: 'admin' as const,
      label: 'Admin',
      description: 'Full access to all features and user management',
      icon: Shield,
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await apiRequest<InvitationResponse>('/api/admin/invitations', {
        method: 'POST',
        body: {
          email,
          name: name || undefined,
          role,
          plan_id: selectedPlanId || undefined,
          trial_days: selectedPlan && selectedPlan.price_cents > 0 ? trialDays : undefined,
        },
      });

      setInvitation(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (invitation?.invitation_link) {
      await navigator.clipboard.writeText(invitation.invitation_link);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleInviteAnother = () => {
    setInvitation(null);
    setEmail('');
    setName('');
    setRole('user');
    setSelectedPlanId('free');
    setTrialDays(14);
    setError(null);
  };

  if (invitation) {
    const emailSent = invitation.data.email_sent;
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => { setInvitation(null); navigate('/admin/users'); }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Invitation Sent</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {invitation.data.email} will be matched automatically when they sign up
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Success Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
            {/* Email Status */}
            <div
              className={`flex items-center gap-3 p-4 rounded-lg ${
                emailSent
                  ? 'bg-green-50 border border-green-200'
                  : invitation.data.email_error === 'not_configured'
                    ? 'bg-amber-50 border border-amber-200'
                    : 'bg-red-50 border border-red-200'
              }`}
            >
              {emailSent ? (
                <>
                  <Mail className="w-5 h-5 text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Invite email sent</p>
                    <p className="text-xs text-green-700">
                      {invitation.data.email} received an invitation to sign up
                    </p>
                  </div>
                </>
              ) : invitation.data.email_error === 'not_configured' ? (
                <>
                  <MailX className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      Email not configured — share the link manually
                    </p>
                    <p className="text-xs text-amber-700">
                      Set RESEND_API_KEY to enable automatic invite emails
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <MailX className="w-5 h-5 text-red-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-900">
                      Failed to send invite email
                    </p>
                    <p className="text-xs text-red-700">
                      {invitation.data.email_error || 'Unknown error'}. Share the link manually instead.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Invitation Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Invitation Details</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{invitation.data.email}</dd>
                </div>
                {invitation.data.name && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{invitation.data.name}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Role</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        invitation.data.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-mazo-100 text-mazo-800'
                      }`}
                    >
                      {invitation.data.role === 'admin' ? 'Admin' : 'User'}
                    </span>
                  </dd>
                </div>
                {invitation.data.metadata?.plan_name && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Plan</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {invitation.data.metadata.plan_name}
                      {invitation.data.metadata.trial_days && (
                        <span className="text-gray-500">
                          {' '}
                          ({invitation.data.metadata.trial_days}-day free trial)
                        </span>
                      )}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Expires</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(invitation.data.expires_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Share Link */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Sign-up link</h3>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono text-gray-700 truncate">
                  {invitation.invitation_link}
                </code>
                <button
                  onClick={copyLink}
                  className="flex items-center gap-1 px-3 py-2 bg-mazo-600 text-white rounded hover:bg-mazo-700 transition-colors text-sm font-medium shrink-0"
                >
                  {copiedLink ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                When the user signs up with {invitation.data.email}, they&apos;ll be automatically
                matched to this invitation.
              </p>
            </div>

            {/* Actions */}
            <div className="border-t border-gray-200 pt-6 flex gap-3">
              <button
                onClick={() => { setInvitation(null); navigate('/admin/users'); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Back to Users
              </button>
              <button
                onClick={handleInviteAnother}
                className="flex-1 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors font-medium"
              >
                Invite Another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/users')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-mazo-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-mazo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Invite User</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Send an invitation — they sign up with Google or email
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                placeholder="user@example.com"
              />
              <p className="mt-1 text-xs text-gray-500">
                The user must sign up with this exact email to be matched
              </p>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Role *</label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {roles.map((r) => {
                  const Icon = r.icon;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRole(r.value)}
                      className={`relative flex items-start p-4 border-2 rounded-lg transition-colors ${
                        role === r.value
                          ? 'border-mazo-600 bg-mazo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Icon
                          className={`w-5 h-5 ${
                            role === r.value ? 'text-mazo-600' : 'text-gray-400'
                          }`}
                        />
                        <div className="text-left">
                          <div
                            className={`text-sm font-medium ${
                              role === r.value ? 'text-mazo-900' : 'text-gray-900'
                            }`}
                          >
                            {r.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{r.description}</div>
                        </div>
                      </div>
                      {role === r.value && (
                        <div className="absolute top-3 right-3">
                          <div className="w-5 h-5 bg-mazo-600 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Plan Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Starting Plan
                </div>
              </label>
              <select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
              >
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.price_cents > 0
                      ? ` — €${(p.price_cents / 100).toFixed(0)}/${p.billing_interval}`
                      : ' — Free'}
                  </option>
                ))}
              </select>

              {isPaidPlan && (
                <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="block text-sm font-medium text-amber-900 mb-2">
                    Free trial duration
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={trialDays}
                      onChange={(e) => setTrialDays(Number(e.target.value) || 14)}
                      className="w-24 px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                    <span className="text-sm text-amber-800">days</span>
                  </div>
                  <p className="mt-2 text-xs text-amber-700">
                    User gets full access to the {selectedPlan?.name} plan during the trial. No
                    payment required until the trial ends.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className="mt-8 flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !email}
              className="flex-1 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
